/**
 * ANNOUNCEMENT COPY (B10) — the §H.3 texts as pure functions. Tested in announceText.test.ts.
 * Calm and specific: no exclamation marks, no superlatives. Minutes are always
 * named "off-peak" (the drive times are OSRM free-flow estimates).
 */
import type { CategoryKind, PlaceStatus } from "@/contracts/data";
import type { LayerFlags } from "@/contracts/store";

/** km as the UI prints it: 1 dp below 100, integer from 100. */
export function kmText(km: number): string {
  return km < 100 ? (Math.round(km * 10) / 10).toFixed(1).replace(/\.0$/, "") : String(Math.round(km));
}

/** "1 minute", "13 minutes", "1 hour", "1 hour 25 minutes", "2 hours 5 minutes". */
export function minutesText(min: number): string {
  const m = Math.max(0, Math.round(min));
  const unit = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  if (m < 60) return unit(m, "minute", "minutes");
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${unit(h, "hour", "hours")} ${unit(r, "minute", "minutes")}` : unit(h, "hour", "hours");
}

const STATUS_TEXT: Record<PlaceStatus, string | null> = {
  existing: null,
  upcoming: "Upcoming",
  proposed: "Proposed",
  "under-construction": "Under construction",
};

export interface SpokenPlace {
  name: string;
  status: PlaceStatus;
  crowKm: number;
  /** 8-point compass word, e.g. "South-west" (compass(bearing) from src/lib/geo). */
  compassWord: string;
  /** Display travel (overrides already applied); null when there is no route. */
  travel: { km: number; minutes: number } | null;
  kind?: CategoryKind;
  /** The site itself: it is never given a distance from itself (QA L3-12). */
  isSite?: boolean;
}

/**
 * Select, at arrival: "{name}. {min} minutes off-peak, {km} kilometres by road, {compass} of the site."
 * Road places speak their nearest access; future places their straight-line distance and status.
 */
export function selectArrivedText(p: SpokenPlace): string {
  if (p.isSite) return `${p.name}, the site.`;
  const where = p.compassWord.toLowerCase();
  const status = STATUS_TEXT[p.status];
  if (p.kind === "future" || !p.travel) {
    const lead = p.kind === "future" && status ? `${p.name}. ${status}, ` : `${p.name}. `;
    return `${lead}${kmText(p.crowKm)} kilometres ${where} of the site.`;
  }
  const access = p.kind === "road" ? "Nearest access: " : "";
  const tail = status ? ` ${status}.` : "";
  return `${p.name}. ${access}${minutesText(p.travel.minutes)} off-peak, ${kmText(p.travel.km)} kilometres by road, ${where} of the site.${tail}`;
}

/** Plural / singular nouns for the bloom summaries (§F). */
export const BLOOM_NOUNS: Record<string, [one: string, many: string]> = {
  "arterial-roads": ["arterial road", "arterial roads"],
  "future-infrastructure": ["planned corridor", "planned corridors"],
  "logistic-hubs": ["airport or station", "airports and stations"],
  education: ["school or college", "schools and colleges"],
  healthcare: ["hospital", "hospitals"],
  "lifestyle-retail": ["place to shop", "places to shop"],
  "urban-centres": ["urban centre", "urban centres"],
  hospitality: ["place to stay", "places to stay"],
  "business-parks": ["business park", "business parks"],
  industrial: ["industrial belt", "industrial belts"],
  "premium-benchmarks": ["premium benchmark", "premium benchmarks"],
  "township-benchmarks": ["township", "townships"],
  "neighbouring-benchmarks": ["neighbouring project", "neighbouring projects"],
  "upcoming-townships": ["upcoming township", "upcoming townships"],
};

/**
 * Chip / bloom done: "Education. 23 schools and colleges, nearest 9 minutes off-peak."
 * `upcoming`: every counted place is upcoming ("Hospitality. 2 upcoming places to stay, …").
 */
export function bloomDoneText(categoryId: string, categoryLabel: string, n: number, nearestMin: number | null, upcoming = false): string {
  const [one, many] = BLOOM_NOUNS[categoryId] ?? ["place", "places"];
  const noun = n === 1 ? one : many;
  const count = `${n} ${upcoming && !/^upcoming\b/.test(noun) ? `upcoming ${noun}` : noun}`;
  const near = categoryId !== "future-infrastructure" && nearestMin != null ? `, nearest ${minutesText(nearestMin)} off-peak` : "";
  return `${categoryLabel}. ${count}${near}.`;
}

/** Group change / "All": "Social infrastructure. 44 places." */
export function groupText(groupLabel: string, n: number): string {
  return `${groupLabel}. ${n === 1 ? "1 place" : `${n} places`}.`;
}

/** Search, after 600 ms: "12 places match." · "1 place matches." · "No places match." */
export function searchText(n: number): string {
  if (n === 0) return "No places match.";
  return n === 1 ? "1 place matches." : `${n} places match.`;
}

export const moodText = (label: string): string => `Map light: ${label}.`;
export const driveStartText = (name: string): string => `Taking the drive to ${name}. Press Escape to stop.`;
export const driveArrivedText = (name: string): string => `Arrived at ${name}.`;
export const DRIVE_STOPPED_TEXT = "Drive stopped.";
export const DESELECT_TEXT = "Details closed.";
export const INTRO_DONE_TEXT = "Map ready.";
/** loader.error + loader.error.detail (assertive). */
export const LOAD_ERROR_TEXT = "The map couldn't load. Check the connection, then try again.";

/** §F layers.* labels. */
export const LAYER_LABELS: Record<keyof LayerFlags, string> = {
  routes: "Drive routes",
  future: "Future infrastructure",
  contours: "Contours",
  hillshade: "Terrain shading",
  rings: "Distance rings",
  boundary: "Site outline",
  labels: "Place names",
};

/** Layer toggled: "Contours shown." · "Distance rings hidden." */
export function layerText(key: keyof LayerFlags, on: boolean): string {
  return `${LAYER_LABELS[key]} ${on ? "shown" : "hidden"}.`;
}
